import { useState, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';

import { MenuItem } from '@material-ui/core';

import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import NgpToggle from '../../form/NgpToggle';
import NgpSelect from '../../form/NgpSelect';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { RowFlexLayout } from '../../layout';

import { useDataService } from '../../../services/ApiDataService';

import { GENERAL_DEFAULT, FIELD_MAPPINGS, PollingApi } from './PollingConstants';

import CredentialFieldArray from './CredentialFieldArray';

const INTERNAL_TYPES = [
    { value: 1, display: '1 minute' },
    { value: 5, display: '5 minutes' },
];

const GeneralTab = forwardRef((props, ref) => {
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: GENERAL_DEFAULT,
        shouldUnregister: false,
    });
    const [configIsDirty, setConfigIsDirty] = useState(false);
    const { reset, setError, watch } = methods;

    const isServerEnabled = watch(FIELD_MAPPINGS.SERVER_ENABLED);
    const isDeviceEnabled = watch(FIELD_MAPPINGS.DEVICE_ENABLED);
    const hasWinrmCert = !!watch('hasWinrmCert');
    const dataService = useDataService();
    const formSaveService = useFormSaveService();

    const postFindHandler = (response) => {
        return {
            ...response,
            rateLimitMegaBitsPerSec: Number(response?.rateLimit ?? 0) / 1_000_000,
        };
    };

    const { initialized, finding, resetForm } = useFind({
        url: PollingApi,
        postFindHandler,
        resetFunc: reset,
        defaultValues: GENERAL_DEFAULT,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const { rateLimitMegaBitsPerSec, ...res } = dataToSave;
        res.rateLimit = dataToSave.rateLimitMegaBitsPerSec * 1_000_000;
        res.credentials = _.map(res.credentials || [], (credential) => _.omit(credential, [INTERNAL_ID_FIELD]));

        return formSaveService.save({
            url: PollingApi,
            saveFunc: () =>
                dataService.put(PollingApi, res, { _id: res._id }).then(() => {
                    setConfigIsDirty(false);
                    resetForm();
                }),
            dataToSave: res,
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const dirtyCheck = (isDirty) => isDirty || configIsDirty;
    useImperativeHandle(ref, () => ({
        save: methods.handleSubmit(handleSubmitFunction),
    }));

    return (
        <FormProvider {...methods}>
            <NgpForm
                submitFunction={handleSubmitFunction}
                initialized={initialized}
                loading={finding || saving}
                isDirtyFunc={dirtyCheck}
                showRequiredFieldsTip={false}
            >
                <FormSection title={<Trans>Settings</Trans>}>
                    <NgpNativeNumberField
                        fieldName={'rateLimitMegaBitsPerSec'}
                        label={<Trans>Traffic bandwidth (Mb/s)</Trans>}
                        min={1}
                        max={100000}
                        step={1}
                        required
                        inputWidth={350}
                    />
                    <RowFlexLayout vCenter>
                        <NgpToggle
                            fieldName={FIELD_MAPPINGS.SERVER_ENABLED}
                            label={isServerEnabled ? t`Monitor servers, polling every` : t`Monitor servers`}
                        />
                        {isServerEnabled && (
                            <NgpSelect fieldName={FIELD_MAPPINGS.SERVER_INTERVAL}>
                                {_.map(INTERNAL_TYPES, (item) => (
                                    <MenuItem key={`server-${item.value}`} value={item.value}>
                                        {item.display}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        )}
                    </RowFlexLayout>
                    <RowFlexLayout vCenter>
                        <NgpToggle
                            fieldName={FIELD_MAPPINGS.DEVICE_ENABLED}
                            label={
                                isDeviceEnabled ? t`Monitor network devices, polling every` : t`Monitor network devices`
                            }
                        />
                        {isDeviceEnabled && (
                            <NgpSelect fieldName={FIELD_MAPPINGS.DEVICE_INTERVAL}>
                                {_.map(INTERNAL_TYPES, (item) => (
                                    <MenuItem key={`device-${item.value}`} value={item.value}>
                                        {item.display}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        )}
                    </RowFlexLayout>
                </FormSection>
                <CredentialFieldArray setSelectionIsDirty={setConfigIsDirty} hasWinrmCert={hasWinrmCert} />
            </NgpForm>
        </FormProvider>
    );
});

export default GeneralTab;
